import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "../layout/mainLayout";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { showError } from "../storage/storageUtils";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import { TransportOrderNotificationListItemDto, UnconfirmedNotificationStates } from "../../models/transport/transportOrderNotification";
import { fetchAllTransportOrderNotifications } from "../../services/transportOrderService";
import { useInterval } from "usehooks-ts";
import { NotificationSource } from "../../models/transport/notificationSource";
import { NotificationsTable } from "./NotificationsTable";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { fetchEmployees } from "../../store/employeeSlice";

const REFETCH_INTERVAL = 1000 * 60;

export const NotificationsListMain = () => {
    const dispatch = useAppDispatch();
    const ownerParameters = new OwnerParameters();
    const hasTransportOrdersEnabled = ownerParameters.getOwnerHasTransportEnabled();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    const employees = useAppSelector((state) => state.employees.employees);
    const [notifications, setNotifications] = useState<TransportOrderNotificationListItemDto[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    const fetchNotifications = async(sortField?: string, sortOrder?: string): Promise<PagedResult<TransportOrderNotificationListItemDto>> => {
        setIsLoading(true);

        try {
            const result = await fetchAllTransportOrderNotifications({
                source: NotificationSource.Mobile,
                states: UnconfirmedNotificationStates,
                page: currentPage,
                pageSize: pageSize,
                sortField: sortField ? sortField : null,
                sortOrder: sortOrder ? sortOrder : null,
            });

            setNotifications(result?.items);
            setTotalCount(result?.totalCount);

            return result;
        } catch (error) {
            showError(t("notifications.fetchFailed"));

            return {
                items: [],
                currentPage: 1,
                pageSize: 50,
                totalCount: 0,
                totalPages: 0,
                hasPreviousPage: false,
                hasNextPage: false
            };
        } finally {
            setIsLoading(false); // Prevent loading state from persisting
        }
    };

    useInterval(() => {
        fetchNotifications().catch((err) => {
            console.error("Error during scheduled fetch:", err);
        });
    }, REFETCH_INTERVAL);

    useEffect(() => {
        fetchNotifications().catch(() => {
            showError(t("notifications.fetchFailed"));
        });
    }, [currentPage, pageSize]);

    useEffect(() => {
        if (employees.length === 0) {
            dispatch(fetchEmployees() as any);
        }
    }, [employees]);

    if (!hasTransportOrdersEnabled) {
        return null;
    }

    if (!employees) {
        return <LoadingIndicator tooltip={t("common.loading")} />;
    }

    return (
        <MainLayout
            noContainer
        >
            <>
                {isLoading && (
                    <LoadingIndicator tooltip={t("common.loading")} />
                )}
                <NotificationsTable
                    notifications={notifications}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                    fetchNotifications={fetchNotifications}
                />
            </>
        </MainLayout>
    );
};
